import React, { useState, useEffect } from "react";
import "./Styling.css";
import { useNavigate } from "react-router-dom";
import { fetchWhitelistedDomains, fetchBlockedDomains, deleteWhitelistedDomain, deleteBlockedDomain, addBlockedDomain, addWhitelistedDomain } from "../../services/dataAnalyticsApi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ManageAccessList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("whitelist"); // whitelist or blocklist
  const [whitelistedDomains, setWhitelistedDomains] = useState([]);
  const [blockedDomains, setBlockedDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
 
  const p_token = "health_check_token";

  // Fetch domains on component mount
  useEffect(() => {
    fetchDomains();
  }, []);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const [whitelistResponse, blockedResponse] = await Promise.all([
        fetchWhitelistedDomains(),
        fetchBlockedDomains()
      ]);

      if (whitelistResponse.status === "successful") {
        setWhitelistedDomains(whitelistResponse.whitelisted_domains || []);
      }
      
      if (blockedResponse.status === "successful") {
        setBlockedDomains(blockedResponse.blocked_domains || []);
      }
    } catch (error) {
      console.error("Error fetching domain lists:", error);
      toast.error("Failed to fetch domain lists");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/dataAnalytics");
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredDomains = (domains) => {
    return domains.filter(domain => 
      domain.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleDeleteDomain = async (domain, type) => {
    if (window.confirm(`Are you sure you want to delete ${domain} from the ${type === "whitelist" ? "whitelist" : "blocklist"}?`)) {
      try {
        setIsSubmitting(true);
        
        if (type === "whitelist") {
          const response = await deleteWhitelistedDomain(domain , p_token);
          if (response.status === "successful") {
            setWhitelistedDomains(prev => prev.filter(d => d !== domain));
            toast.success(`${domain} removed from whitelist`);
          } else {
            toast.error(response.message || `Failed to remove ${domain}`);
          }
        } else {
          const response = await deleteBlockedDomain(domain, p_token);
          if (response.status === "successful") {
            setBlockedDomains(prev => prev.filter(d => d !== domain));
            toast.success(`${domain} removed from blocklist`);
          } else {
            toast.error(response.message || `Failed to remove ${domain}`);
          }
        }
      } catch (error) {
        console.error(`Error deleting domain ${domain}:`, error);
        toast.error(`Failed to delete ${domain}`);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    
    if (!newDomain.trim()) {
      toast.warning("Please enter a valid domain");
      return;
    }

    try {
      setIsSubmitting(true);
      
      if (activeTab === "whitelist") {
        const response = await addWhitelistedDomain(newDomain.trim() , p_token);
        if (response.status === "successful") {
          setWhitelistedDomains(prev => [...prev, newDomain.trim()]);
          toast.success(`${newDomain.trim()} added to whitelist`);
        } else {
          toast.error(response.message || `Failed to add ${newDomain.trim()} to whitelist`);
        }
      } else {
        const response = await addBlockedDomain(newDomain.trim(), p_token);
        if (response.status === "successful") {
          setBlockedDomains(prev => [...prev, newDomain.trim()]);
          toast.success(`${newDomain.trim()} added to blocklist`);
        } else {
          toast.error(response.message || `Failed to add ${newDomain.trim()} to blocklist`);
        }
      }
      
      setNewDomain("");
    } catch (error) {
      console.error(`Error adding domain ${newDomain}:`, error);
      toast.error(`Failed to add ${newDomain}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="advanced-metrics-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="metrics-header">
        <h2>Manage Access List</h2>
        <button className="back-button" onClick={handleBack}>
          ⬅ Back To Dashboard
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-header">
        <button
          className={`main-tab ${activeTab === "whitelist" ? "active" : ""}`}
          onClick={() => setActiveTab("whitelist")}
        >
          Whitelisted Domains ({whitelistedDomains.length})
        </button>
        <button
          className={`main-tab ${activeTab === "blocklist" ? "active" : ""}`}
          onClick={() => setActiveTab("blocklist")}
        >
          Blocked Domains ({blockedDomains.length})
        </button>
      </div>
      
      {/* Search and Add Form */}
      <div className="domain-management">
        <div className="search-add-container">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search domains..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          
          <form onSubmit={handleAddDomain} className="add-domain-form">
            <input
              type="text"
              placeholder={`Add domain to ${activeTab === "whitelist" ? "whitelist" : "blocklist"}...`}
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
            />
            <button 
              type="submit" 
              disabled={isSubmitting || !newDomain.trim()}
              className="add-domain-btn"
            >
              {isSubmitting ? "Adding..." : "Add Domain"}
            </button>
          </form>
        </div>
        
        {/* Domain Table */}
        <div className="domain-table-container">
          {loading ? (
            <div className="loading">Loading domain data...</div>
          ) : (
            <table className="domain-table">
              <thead>
                <tr>
                  <th>Domain</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === "whitelist" ? (
                  filteredDomains(whitelistedDomains).length > 0 ? (
                    filteredDomains(whitelistedDomains).map((domain, index) => (
                      <tr key={`whitelist-${index}`}>
                        <td>{domain}</td>
                        <td>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteDomain(domain, "whitelist")}
                            disabled={isSubmitting}
                          >
                            Remove 
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="no-results">
                        {searchTerm ? "No whitelisted domains matching your search" : "No whitelisted domains found"}
                      </td>
                    </tr>
                  )
                ) : 
                (
                  filteredDomains(blockedDomains).length > 0 ? (
                    filteredDomains(blockedDomains).map((domain, index) => (
                      <tr key={`blocklist-${index}`}>
                        <td>{domain}</td>
                        <td>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteDomain(domain, "blocklist")}
                            disabled={isSubmitting}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2" className="no-results">
                        {searchTerm ? "No blocked domains matching your search" : "No blocked domains found"}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageAccessList;
